import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { Query } from 'react-resource-router';

import { fg } from '@atlaskit/platform-feature-flags';

import { generateJiraSearchUrl } from '../jira/generate-search-url';
import { extractTextFieldValueFromJql } from '../jira/jql';
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from '../local-storage';

type Filters = Record<string, string>;

const ISSUE_NAVIGATOR = 'issue-navigator' as const;
const FULL_PAGE_SEARCH = 'full-page-search' as const;

export type Experience = typeof ISSUE_NAVIGATOR | typeof FULL_PAGE_SEARCH;

const ROVO_LOCAL_STORAGE_PREFIX_KEY = 'rovo-navigation-filters';

const getLocalStorage = () => {
	if (typeof window !== 'undefined') {
		// TODO(MIGRATE-STORAGE-BASELINE): migrate to @atlassian/search-common/local-storage wrapper
		// eslint-disable-next-line @atlassian/search-common/no-direct-local-storage, @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		return window.localStorage;
	}
};

const getFiltersFromLocalStorage = (experience: Experience): Filters | null => {
	if (fg('search_bsc_local_storage_migration')) {
		return (
			(getLocalStorageItem(`${ROVO_LOCAL_STORAGE_PREFIX_KEY}-${experience}`) as Filters | null) ??
			null
		);
	}
	const filters = getLocalStorage()?.getItem(`${ROVO_LOCAL_STORAGE_PREFIX_KEY}-${experience}`);
	return filters ? (JSON.parse(filters) as Filters) : null;
};

const saveFiltersToLocalStorage = (experience: string, filters: Filters) => {
	if (fg('search_bsc_local_storage_migration')) {
		setLocalStorageItem(`${ROVO_LOCAL_STORAGE_PREFIX_KEY}-${experience}`, JSON.stringify(filters));
	} else {
		getLocalStorage()?.setItem(
			`${ROVO_LOCAL_STORAGE_PREFIX_KEY}-${experience}`,
			JSON.stringify(filters),
		);
	}
};

const clearFilters = () => {
	if (fg('search_bsc_local_storage_migration')) {
		removeLocalStorageItem(`${ROVO_LOCAL_STORAGE_PREFIX_KEY}-${ISSUE_NAVIGATOR}`);
		removeLocalStorageItem(`${ROVO_LOCAL_STORAGE_PREFIX_KEY}-${FULL_PAGE_SEARCH}`);
	} else {
		getLocalStorage()?.removeItem(`${ROVO_LOCAL_STORAGE_PREFIX_KEY}-${ISSUE_NAVIGATOR}`);
		getLocalStorage()?.removeItem(`${ROVO_LOCAL_STORAGE_PREFIX_KEY}-${FULL_PAGE_SEARCH}`);
	}
};

const extractTextFromQueryParams = (experience: Experience, queryString: Query) =>
	experience === FULL_PAGE_SEARCH
		? queryString.text?.toString()
		: extractTextFieldValueFromJql(queryString.jql?.toString());

const syncJqlWithText = (filters: Filters, query: string | undefined): Filters =>
	filters.text ? { ...filters, text: query || '' } : filters;

export function useRovoNavigationUrl({
	experience,
	queryString,
	sainJql,
}: {
	experience: Experience;
	queryString: Query;
	sainJql?: string | null;
}): {
	issueNavigatorUrl: string;
	fullPageSearchUrl: string;
	onRovoNavigate: () => void;
	text: string | undefined;
} {
	// we read the saved filters for the experience that we need to create the link for.
	const filterQueryParams = useRef(
		getFiltersFromLocalStorage(
			experience === FULL_PAGE_SEARCH ? ISSUE_NAVIGATOR : FULL_PAGE_SEARCH,
		),
	);

	const text = useMemo(
		() => extractTextFromQueryParams(experience, queryString),
		[experience, queryString],
	);
	const initialText = useRef(text);

	const hasTextChanged = text !== initialText.current;

	const issueNavigatorUrl = useMemo(() => {
		if (experience === ISSUE_NAVIGATOR) {
			return '';
		}

		if (sainJql) {
			return `/issues/?jql=${encodeURIComponent(sainJql)}&referrer=search-page`;
		}

		return generateJiraSearchUrl(
			queryString,
			true,
			{
				...(hasTextChanged ? {} : filterQueryParams.current),
				referrer: 'search-page',
			},
			false,
		);
	}, [hasTextChanged, queryString, experience, sainJql]);

	const fullPageSearchUrl = useMemo(() => {
		if (experience === FULL_PAGE_SEARCH) {
			return '';
		}

		return generateJiraSearchUrl(queryString, false, {
			...(hasTextChanged ? {} : filterQueryParams.current),
			text: text || '',
		});
	}, [hasTextChanged, queryString, text, experience]);

	// clear the filters on mount to prevent stale state
	useEffect(clearFilters, []);

	// Callback to save filters on link click to use it in the other view
	const onRovoNavigate = useCallback(() => {
		let filters = queryString;
		if (experience === ISSUE_NAVIGATOR) {
			filters = syncJqlWithText(queryString, text);
		}
		saveFiltersToLocalStorage(experience, filters);
	}, [experience, queryString, text]);

	return { issueNavigatorUrl, fullPageSearchUrl, onRovoNavigate, text };
}
